// An anchored circular bowl supports a persistent main tongue and two alternating
// side tongues. Common tangents and circular notches keep every moving contact
// smooth. Side circles merge into the bowl before becoming inactive.
function smoothNoise(time, channel) {
    const segment = Math.floor(time);
    const progress = time - segment;
    const eased = progress ** 3 * (10 + progress * (-15 + 6 * progress));
    // Separate hash channels reuse the deterministic sampler without consuming
    // or changing the accepted ember sequence. Start at nominal size on Replay.
    const sample = (index) => index === 0 ? 0 : variation(index, channel) * 2 - 1;
    return sample(segment) + (sample(segment + 1) - sample(segment)) * eased;
}
function bodyAt(time) {
    // Two unrelated time scales avoid a regular breathing beat or simultaneous
    // pauses at every interpolation knot. Quintic interpolation keeps velocity
    // and acceleration continuous; the combined radius stays within ±10%.
    const drift = 0.7 * smoothNoise(time / 0.75, 17) + 0.3 * smoothNoise(time / 1.30, 19);
    return { x: 12, y: 15, radius: 7 * (1 + 0.10 * drift) };
}
// Reduce every tip radius by 15% to sharpen the silhouette, preserving rounded
// caps at small sizes and the existing 0.8 side-to-main radius proportion.
const tipRadiusFactor = 0.85;
const pointOn = (disc, angle) => ({
    x: disc.x + Math.cos(angle) * disc.radius,
    y: disc.y + Math.sin(angle) * disc.radius,
});
const xy = (point) => `${point.x.toFixed(4)} ${point.y.toFixed(4)}`;
function tipDisc(body, x, y, radiusScale = 1) {
    // Tips broaden as they approach the body, and narrow as they lift away.
    // Measure clearance from the live bowl edge, so a larger bowl also broadens
    // the tips. R + 4.5 is the original 11.5-distance rule at nominal radius 7.
    const radiusAt = (offset) => tipRadiusFactor * radiusScale * Math.max(0.85, Math.min(2.4, 0.95 + (body.radius + 4.5 - Math.hypot(offset, y - body.y)) * 0.3));
    let offset = Math.abs(x - body.x);
    // Constrain the whole tip disc, not just its centre, to the body's width.
    // Moving inward also increases its radius, so solve both together instead
    // of clamping with a stale radius that could still protrude past the bowl.
    if (offset + radiusAt(offset) > body.radius) {
        let lower = 0, upper = Math.min(offset, body.radius);
        for (let iteration = 0; iteration < 24; iteration++) {
            const middle = (lower + upper) / 2;
            if (middle + radiusAt(middle) <= body.radius)
                lower = middle;
            else
                upper = middle;
        }
        offset = lower;
    }
    return { x: body.x + Math.sign(x - body.x) * offset, y, radius: radiusAt(offset) };
}
function tangents(body, tip) {
    const direction = Math.atan2(tip.y - body.y, tip.x - body.x);
    const spread = Math.acos((body.radius - tip.radius) / Math.hypot(tip.x - body.x, tip.y - body.y));
    return {
        left: pointOn(tip, direction - spread), right: pointOn(tip, direction + spread),
        bodyLeft: pointOn(body, direction - spread), bodyRight: pointOn(body, direction + spread),
    };
}
function innerTangent(tip, join, side) {
    const direction = Math.atan2(join.y - tip.y, join.x - tip.x);
    // The notch is concave, so its radius faces the opposite side of the line.
    // Internal common tangents use the sum of the two radii.
    const spread = Math.acos((tip.radius + join.radius) / Math.hypot(join.x - tip.x, join.y - tip.y));
    const angle = direction + side * spread;
    return { tip: pointOn(tip, angle), join: pointOn(join, angle + Math.PI) };
}
const ease = (value) => value * value * (3 - 2 * value);
// Only soften the final 0.75 units of exposure above the bowl. Geometry, not
// a second timer, drives the blend; smoothstep has zero slope at both ends.
const mergeBlend = (clearance) => ease(Math.max(0, Math.min(1, 1 - clearance / 0.75)));
function softenMergingTip(body, tip) {
    const dx = tip.x - body.x, dy = tip.y - body.y, distance = Math.hypot(dx, dy);
    const clearance = distance + tip.radius - body.radius;
    const nx = dx / distance, ny = dy / distance;
    // Inflate inward: radial exposure stays fixed while the merging cap softens.
    // Both side tongues and the main tongue use this rule when becoming a circle.
    // The whole-disc width bound is |nx| * (distance + old radius - r) + r ≤ R.
    // At a horizontal internal tangency both numerator and denominator tend
    // to zero. Its exact limit is R; avoid rounding a hidden guide to NaN.
    const widthLimit = 1 - Math.abs(nx) < 1e-8 && clearance < 1e-8 ? body.radius
        : (body.radius - Math.abs(nx) * (distance + tip.radius)) / (1 - Math.abs(nx));
    const radius = Math.min(tip.radius + (body.radius / 2 - tip.radius) * mergeBlend(clearance), widthLimit);
    const shift = radius - tip.radius;
    return { x: tip.x - shift * nx, y: tip.y - shift * ny, radius };
}
// Retract a rounded tongue into its own bowl. Sharing this construction keeps
// the core and outer flame subject to the same curvature and width rules.
function retractTip(body, tip, exposureFraction) {
    if (exposureFraction === 1)
        return tip;
    // Grow the cap toward R while reducing radial exposure h. Its curvature
    // converges to the bowl's curvature, avoiding a small bump at the merge.
    const dx = tip.x - body.x, dy = tip.y - body.y, distance = Math.hypot(dx, dy);
    const exposure = (distance + tip.radius - body.radius) * exposureFraction;
    const nx = dx / distance * exposureFraction, ny = -Math.sqrt(1 - nx * nx);
    // Fade lateral lean as the circles coincide. The full cap stays inside the
    // bowl width: |nx| × (R − r + h) + r ≤ R.
    const widthLimit = body.radius - Math.abs(nx) * exposure / (1 - Math.abs(nx));
    const radius = Math.min(body.radius + (tip.radius - body.radius) * ease(exposureFraction), widthLimit);
    const remaining = body.radius - radius + exposure;
    return { x: body.x + nx * remaining, y: body.y + ny * remaining, radius };
}
function tongueHandoff(index) {
    // Stable per-handoff variation breaks the mirrored 1.7 s metronome without
    // adding mutable random state. Channels 30–33 are independent of the embers.
    // Jitter stays below half the nominal spacing, so order cannot reverse.
    const jitter = index === 0 ? 0 : (variation(index, 30) - 0.5) * 0.44;
    return {
        index, start: index * 1.7 - 0.7 + jitter,
        rise: 0.44 + variation(index, 31) * 0.28,
        fall: 0.65 + variation(index, 32) * 0.35,
        strength: 0.82 + variation(index, 33) * 0.18,
    };
}
function handoffAt(time) {
    const nominal = Math.floor((time + 0.7) / 1.7);
    const candidate = tongueHandoff(nominal);
    if (candidate.start > time)
        return tongueHandoff(nominal - 1);
    const next = tongueHandoff(nominal + 1);
    return next.start <= time ? next : candidate;
}
function tonguePresences(time) {
    const incoming = handoffAt(time);
    const outgoing = tongueHandoff(incoming.index - 1);
    const age = time - incoming.start;
    const rise = incoming.strength * ease(Math.min(age / incoming.rise, 1));
    // The outgoing tongue starts receding only when its successor actually
    // emerges. This preserves overlap even when the start times vary.
    const fall = outgoing.strength * (1 - ease(Math.min(age / outgoing.fall, 1)));
    return incoming.index % 2 === 0 ? { right: rise, left: fall } : { right: fall, left: rise };
}
function flameTilt(time) {
    // Full endpoint-to-endpoint swings still read as a pendulum even with small
    // timing variations. Let each tongue give a finite push that relaxes on its
    // own schedule instead. Weak pushes need not mirror their stronger neighbor.
    const latest = handoffAt(time).index;
    let rotation = 0;
    for (let index = latest - 2; index <= latest; index++) {
        const emergence = tongueHandoff(index).start;
        const delay = 0.10 + variation(index, 35) * 0.22;
        const rise = 0.34 + variation(index, 36) * 0.36;
        const linger = 0.18 * variation(index, 37) ** 2;
        const release = 0.75 + variation(index, 38) * 0.65;
        const age = time - emergence - delay;
        if (age <= 0 || age >= rise + linger + release)
            continue;
        const progress = age < rise ? age / rise
            : 1 - Math.max(0, (age - rise - linger) / release);
        // Quintic attack/release joins have zero speed and acceleration. Overlap
        // blends opposing pushes continuously, including at handoff boundaries.
        const envelope = progress ** 3 * (10 + progress * (-15 + 6 * progress));
        const strength = 3 + 10 * variation(index, 34) ** 0.65;
        rotation += (index % 2 === 0 ? -1 : 1) * strength * envelope;
    }
    // A small independent drift keeps rests from locking to the same angle or
    // beat. Same-side pushes are at least 2.74 s apart, longer than their 2.28 s
    // envelope; opposite pushes cancel. Adding at most 2° stays within ±15°
    // without a hard clamp, which would introduce another mechanical stop.
    return rotation + 1.4 * smoothNoise(time / 2.6, 40) + 0.6 * smoothNoise(time / 0.8, 39);
}
function joinBetween(body, left, right, main) {
    // Solve the circle tangent externally to the bowl and both tip discs
    // (the gap's Apollonius circle). Choosing a contact angle first and then
    // squeezing the radius to fit produced tiny, pinched joins during merging.
    // Solving position and capacity together gives a continuous merge instead.
    const u = { x: left.x - body.x, y: left.y - body.y };
    const v = { x: right.x - body.x, y: right.y - body.y };
    const determinant = u.x * v.y - u.y * v.x;
    const solve = (leftValue, rightValue) => ({
        x: (leftValue * v.y - u.y * rightValue) / determinant,
        y: (u.x * rightValue - leftValue * v.x) / determinant,
    });
    // Subtracting the squared tangency equations makes the centre offset A+Bρ:
    // u·A = (|u|² + R² - rLeft²)/2; u·B = R-rLeft (and likewise for v).
    const A = solve((u.x * u.x + u.y * u.y + body.radius ** 2 - left.radius ** 2) / 2, (v.x * v.x + v.y * v.y + body.radius ** 2 - right.radius ** 2) / 2);
    const B = solve(body.radius - left.radius, body.radius - right.radius);
    // |A+Bρ|² = (R+ρ)². Use the stable quadratic form and its positive gap root;
    // the fitted circle grows as a side tip sinks, while its normal tends toward
    // the main tongue's tangent contact. The drawn join remains a bounded size.
    const a = B.x * B.x + B.y * B.y - 1;
    const b = 2 * (A.x * B.x + A.y * B.y - body.radius);
    const c = A.x * A.x + A.y * A.y - body.radius ** 2;
    const q = -0.5 * (b + (b >= 0 ? 1 : -1) * Math.sqrt(Math.max(0, b * b - 4 * a * c)));
    const fittedRadius = c / q > 0 ? c / q : q / a;
    const angle = Math.atan2(A.y + B.y * fittedRadius, A.x + B.x * fittedRadius);
    // Broaden the join as neighboring tips approach, measuring the clear gap
    // between their edges. Fully exposed tips keep the tuned 0.35–1.8 range.
    const gap = Math.max(0, Math.hypot(right.x - left.x, right.y - left.y) - left.radius - right.radius);
    const proximity = Math.max(0, Math.min(1, (5.5 - gap) / 3));
    const proximityRadius = 0.35 + 1.45 * ease(proximity);
    const side = main === left ? right : left;
    const clearance = Math.hypot(side.x - body.x, side.y - body.y) + side.radius - body.radius;
    // A tiny notch beside a disappearing cap left a short, sharp S-bend. As
    // the cap merges, broaden its concave counterpart toward half the bowl's
    // radius too. The fitted-circle and main-tip bounds below still apply.
    const desired = proximityRadius + (body.radius / 2 - proximityRadius) * mergeBlend(clearance);
    // Keep the main tip's inner contacts on its upper semicircle. Below the
    // equator the cap overhangs and creates a neck. At the limiting vertical
    // tangent, join.x = main.x + direction * (main.radius + radius). Substitute
    // join.x = body.x + (R + radius) * normalX and solve for the allowed radius.
    const direction = main === left ? 1 : -1;
    const normalX = Math.cos(angle);
    const mainLimit = (direction * (body.x + body.radius * normalX - main.x) - main.radius)
        / (1 - direction * normalX);
    // A conservative C1 blend over 0.08 units avoids a speed kink when a bound
    // takes over. It stays below both limits, reducing either by at most 0.02.
    const smoothMin = (a, b) => Math.min(a, b) - Math.max(0.08 - Math.abs(a - b), 0) ** 2 / (4 * 0.08);
    const radius = smoothMin(smoothMin(desired, fittedRadius * 0.92), mainLimit);
    // Keep the fitted normal while reducing the radius: the join still touches
    // the bowl and has valid internal tangents to both tips. At a merge its arc
    // turns through zero, rather than collapsing the radius to create a cusp.
    const disc = { ...pointOn({ ...body, radius: body.radius + radius }, angle), radius };
    return { disc, contact: pointOn(body, angle), left: innerTangent(left, disc, -1), right: innerTangent(right, disc, 1) };
}
// The main tongue's vertical flicker is bounded by ±1.05 units.
const mainTipFlicker = (time) => Math.sin(time * 9) * 0.9 + Math.sin(time * 15) * 0.15;
export function flameConstruction(time, intensity = 1, accent) {
    const level = Number.isFinite(intensity) ? Math.max(0, Math.min(1, intensity)) : 1;
    const liveBody = bodyAt(time);
    const body = { ...liveBody, radius: (7 + (liveBody.radius - 7) * level) * (accent?.bodyScale ?? 1) };
    // Drive the actual circles, not an SVG stretch: the construction, rounded
    // caps, width constraints and tangent joins all follow the transient lift.
    const lift = accent?.tongueLift ?? 0;
    // Use the exact circular limit before tangent angles become numerically
    // degenerate. At this threshold the residual lift is below a millionth of
    // an SVG unit; do not leave a tiny tongue or a NaN at the resting endpoint.
    if (level <= 0.00001) {
        const hidden = { x: body.x, y: body.y, radius: 0, opacity: 0 };
        const right = pointOn(body, 0), left = pointOn(body, Math.PI);
        return {
            discs: [body, hidden, hidden, hidden, hidden, hidden],
            presence: { left: 0, right: 0 }, rotation: 0,
            outerTangents: [],
            innerTangents: [],
            outline: `M${xy(right)} A${body.radius} ${body.radius} 0 1 1 ${xy(left)} A${body.radius} ${body.radius} 0 1 1 ${xy(right)} Z`,
        };
    }
    // Lead with the main tongue, then let the side tongues emerge. Their ranges
    // overlap from 0.4–0.7 so it reads as one growing object, with the same
    // geometric path in reverse. Level 1 exactly preserves the accepted flame.
    const mainExposure = ease(Math.min(1, level / 0.7));
    const sideExposure = ease(Math.max(0, (level - 0.4) / 0.6));
    // Each emergence has its own timing and strength, with a guaranteed overlap
    // as the previous side recedes. The geometry continues to follow presence.
    const presence = tonguePresences(time);
    const rightPresence = presence.right * sideExposure, leftPresence = presence.left * sideExposure;
    // Keep the tips' lift and settling mostly vertical. A little sideways drift
    // preserves asymmetry while the circular bowl remains planted. The main
    // tip gets a 1.05-unit vertical envelope (0.90 + 0.15), so its lift leads the
    // side tips' 0.40-unit flicker without speeding up their shared rhythm.
    // The higher main centre and lower side targets give the main tongue
    // enough height separation to stay distinct at the 15° tilt.
    let main = tipDisc(body, 12 + (leftPresence - rightPresence) * 0.5 + Math.sin(time * 7) * 0.06, 4.3 + mainTipFlicker(time) - lift);
    main = retractTip(body, main, mainExposure);
    const mainEdges = tangents(body, main);
    const sideTip = (direction, presence, drop) => {
        const contact = direction < 0 ? mainEdges.bodyLeft : mainEdges.bodyRight;
        const angle = Math.atan2(contact.y - body.y, contact.x - body.x);
        // At rest the side disc is internally tangent to the bowl at the main
        // flank's contact. Its exposed cap therefore shrinks smoothly to nothing.
        const hidden = pointOn({ ...body, radius: body.radius - 2.4 * 0.8 * tipRadiusFactor }, angle);
        const targetX = 12 + direction * 5 + Math.sin(time * (direction < 0 ? 6.1 : 8)) * 0.05;
        const targetY = (direction < 0 ? 8.65 : 8.4) + Math.sin(time * (direction < 0 ? 8.1 : 7.3)) * 0.4 + drop - lift * 0.6;
        const tip = tipDisc(body, hidden.x + (targetX - hidden.x) * presence, hidden.y + (targetY - hidden.y) * presence, 0.8);
        return { ...softenMergingTip(body, tip), opacity: Math.min(1, presence * 8) };
    };
    // Protect the tongue that is emerging, even before it becomes the larger
    // one. Choosing by presence would swap ownership mid-handoff. A 3:1 target
    // correction lets the receding side carry more of the taper. The smaller
    // incoming share keeps 38° feasible when its predecessor is nearly gone.
    // When ownership changes, the new side has zero presence, so the solved
    // visible geometry stays continuous despite the change in target weights.
    const incomingRight = handoffAt(time).index % 2 === 0;
    const sidesAt = (drop) => {
        const left = sideTip(-1, leftPresence, drop * (incomingRight ? 3 : 1));
        const right = sideTip(1, rightPresence, drop * (incomingRight ? 1 : 3));
        // A lifted main tip can swallow the last sliver of a receding side.
        // Test the side circle's support against the main flank's tangent line:
        // a nonpositive exposure lies inside the existing silhouette and needs
        // no join. Solving its vanished gap would create an unbounded circle.
        // The disappearing arcs have zero turn at this boundary, so switching
        // to the main flank is continuous. The tolerance is one millionth of an SVG unit.
        const exposed = (tip, contact) => ((tip.x - body.x) * (contact.x - body.x) + (tip.y - body.y) * (contact.y - body.y)) / body.radius
            + tip.radius - body.radius > 1e-6;
        return { left, right,
            leftJoin: leftPresence > 0.00001 && exposed(left, mainEdges.bodyLeft) ? joinBetween(body, left, main, main) : null,
            rightJoin: rightPresence > 0.00001 && exposed(right, mainEdges.bodyRight) ? joinBetween(body, main, right, main) : null };
    };
    const opening = (sides) => [
        sides.leftJoin?.right.tip ?? mainEdges.left,
        sides.rightJoin?.left.tip ?? mainEdges.right,
    ].reduce((sum, point) => sum + Math.asin(Math.max(-1, Math.min(1, (main.y - point.y) / main.radius))), 0) * 180 / Math.PI;
    let sides = sidesAt(0);
    const naturalOpening = opening(sides);
    // Tangency alone allowed near-parallel flanks: both side tongues could form
    // shoulders around a liquid-like central stalk. Keep a 38° total opening by
    // solving the smallest weighted lowering of their targets. Each actual
    // shift still follows presence, retaining the overlap envelopes and fading
    // away at a merge. Refit circles and tangents; never squeeze a join radius
    // just to reach the angle, which would reintroduce the old pinched neck.
    if (naturalOpening < 40) {
        // Smooth max over a 2° band activates the constraint without a velocity
        // kink. Openings above 40° stay untouched; narrower ones remain ≥38°.
        const target = Math.max(38, naturalOpening)
            + Math.max(2 - Math.abs(naturalOpening - 38), 0) ** 2 / 8;
        let lower = 0, upper = 0.25;
        let candidate = sidesAt(upper);
        // Bracket the incoming target offset in small steps, with a four-unit
        // search bound. The receding target uses three times that offset.
        while (opening(candidate) < target && upper < 4) {
            lower = upper;
            upper += 0.25;
            candidate = sidesAt(upper);
        }
        // A 0.25-unit bracket / 2^16 is far below a rendered pixel even at 192 px,
        // avoiding visible quantization when the active constraint follows a tip.
        for (let iteration = 0; iteration < 16; iteration++) {
            const middle = (lower + upper) / 2;
            const attempt = sidesAt(middle);
            if (opening(attempt) < target)
                lower = middle;
            else {
                upper = middle;
                candidate = attempt;
            }
        }
        sides = candidate;
    }
    const { left, right, leftJoin, rightJoin } = sides;
    const leftActive = leftJoin !== null, rightActive = rightJoin !== null;
    const first = leftActive ? left : main, last = rightActive ? right : main;
    const start = tangents(body, first), end = tangents(body, last);
    let outline = `M${xy(start.bodyLeft)} L${xy(start.left)}`;
    const cap = (tip, to) => ` A${tip.radius} ${tip.radius} 0 0 1 ${xy(to)}`;
    const notch = (join) => ` L${xy(join.left.join)} A${join.disc.radius} ${join.disc.radius} 0 0 0 ${xy(join.right.join)} L${xy(join.right.tip)}`;
    if (leftJoin)
        outline += cap(left, leftJoin.left.tip) + notch(leftJoin);
    outline += cap(main, rightJoin ? rightJoin.left.tip : end.right);
    if (rightJoin)
        outline += notch(rightJoin) + cap(right, end.right);
    outline += ` L${xy(end.bodyRight)} A${body.radius} ${body.radius} 0 1 1 ${xy(start.bodyLeft)} Z`;
    const hiddenJoin = { x: body.x, y: body.y - body.radius, radius: 0, opacity: 0 };
    return {
        discs: [body, main, leftActive ? left : { ...left, opacity: 0 }, rightActive ? right : { ...right, opacity: 0 },
            leftJoin ? { ...leftJoin.disc, opacity: left.opacity } : hiddenJoin,
            rightJoin ? { ...rightJoin.disc, opacity: right.opacity } : hiddenJoin],
        presence: { left: leftActive ? leftPresence : 0, right: rightActive ? rightPresence : 0 },
        // Apply this as one rigid SVG rotation, never to the driving coordinates.
        // Its independent eased handoff retains deterministic replay/slow motion.
        rotation: flameTilt(time) * level,
        outerTangents: [{ body: start.bodyLeft, tip: start.left }, { body: end.bodyRight, tip: end.right }],
        innerTangents: [leftJoin, rightJoin].flatMap(join => join ? [join.left, join.right] : []),
        outline,
    };
}
// Show the exact tangent segments used by the silhouette in the construction
// overlay, keeping the guide geometry tied to the visible contour.
export function flameTangentGuides(construction) {
    return [
        ...construction.outerTangents.map(tangent => ({
            kind: 'outer-tangent',
            tangent: `M${xy(tangent.body)} L${xy(tangent.tip)}`,
        })),
        ...construction.innerTangents.map(tangent => ({
            kind: 'inner-tangent',
            tangent: `M${xy(tangent.tip)} L${xy(tangent.join)}`,
        })),
    ];
}
// Different frequencies let the core and outer tongue meet and separate
// naturally. Small seeded phase changes soften the repeating beat without
// adding random jumps. Quintic noise keeps speed and acceleration continuous.
// The weights sum to 1, preserving the core's 0.40–0.80R radial bounds;
// zero phase at t=0 preserves the resting and Replay pose.
function coreTipFlicker(time) {
    const broadPhase = 8.2 * time + 0.45 * smoothNoise(time / 0.95, 43);
    const finePhase = 13.7 * time + 0.30 * smoothNoise(time / 1.60, 44);
    return 0.85 * Math.sin(broadPhase) + 0.15 * Math.sin(finePhase);
}
// A smaller bowl and one tip repeat the outer flame's circle-and-tangent
// construction. All dimensions follow the live outer radius R, including the
// transition's compression. The core has greater radial travel than the main
// tongue, plus its own lateral drift and one quarter of the main tip's lean.
export function flameCoreConstruction(outer, intensity = 1, time = 0, stretch = 0) {
    const level = Number.isFinite(intensity) ? Math.max(0, Math.min(1, intensity)) : 1;
    const bowl = outer.discs[0], main = outer.discs[1];
    const R = bowl.radius;
    // The burning core sits low in the bowl; a circular core should be
    // concentric with it. Ease the offset from the same morph level so the
    // outline and construction centre move together, including on reversal.
    // Give the centre the full opening impulse so its drop is clearly visible.
    // The same positive-only pulse stretches the tip and settles the centre
    // directly back to rest, without a separate spring or upward undershoot.
    // Intensity keeps interrupted transitions concentric at the circle endpoint.
    const body = { x: bowl.x, y: bowl.y + (0.24 + stretch) * R * ease(level), radius: 0.42 * R };
    // Distance is measured radially from the core bowl's centre, not as a y
    // offset. ±0.20R gives ±1.4 units at R=7, exceeding the main tip's ±1.05.
    // The 0.40R minimum stays beyond internal tangency, while the 0.80R
    // maximum keeps the entire core inside the outer bowl.
    // Opening adds one stretch pulse in units of the outer radius. Apply
    // it before the proximity-radius rule, so the tip narrows while stretching
    // and broadens as it settles instead of scaling the finished SVG contour.
    const distance = R * (0.60 - 0.20 * coreTipFlicker(time) + stretch);
    // As with the outer tips, proximity broadens the cap and distance narrows
    // it. Measure from this core's own bowl centre. The resting distance is bounded
    // by 0.40–0.80R (opening extends this to 0.40–0.95R). Broaden only the nearer half with a smooth proximity
    // bonus: 0.18R at the closest distance, keeping 0.10R at nominal distance
    // and 0.06R far away. Zero bonus slope at its ends avoids a size-speed kink.
    const proximity = Math.max(0, Math.min(1, (0.60 * R - distance) / (0.20 * R)));
    const radius = 0.10 * R + 0.20 * (0.60 * R - distance) + 0.04 * R * ease(proximity);
    // Move the tip relative to its own bowl, not just by rotating the whole
    // core. Two smooth seeded tracks avoid a regular side-to-side beat; the
    // ±0.32R target makes the shape change readable alongside the whole-flame tilt.
    const drift = 0.32 * R * (0.7 * smoothNoise(time / 0.65, 41) + 0.3 * smoothNoise(time / 1.10, 42));
    // Keep the drift centred: a fixed lateral offset made the core favour the
    // right even when the flame leaned left. Asymmetry comes from motion itself.
    const offset = 0.25 * (main.x - bowl.x) + drift;
    const widthLimit = body.radius - radius;
    // Round off the width limit over 0.04R instead of abruptly clamping the tip.
    // Its entire disc stays inside its own bowl's width, including at full
    // intensity, so the shared retraction rule cannot snap as it reaches 1.
    const magnitude = Math.min(Math.abs(offset), widthLimit)
        - Math.max(0.04 * R - Math.abs(Math.abs(offset) - widthLimit), 0) ** 2 / (0.16 * R);
    const target = {
        x: body.x + Math.sign(offset) * magnitude,
        // Preserve the requested radial distance as the tip moves sideways. The
        // live radius also updates the width limit (at most 0.36R), always below
        // the live distance throughout opening, so this stays real.
        y: body.y - Math.sqrt(distance * distance - magnitude * magnitude), radius,
    };
    const exposure = ease(Math.min(1, level / 0.7));
    const tip = retractTip(body, target, exposure);
    if (level <= 0.00001) {
        const right = pointOn(body, 0), left = pointOn(body, Math.PI);
        return { discs: [body, { ...body }], tangents: [],
            outline: `M${xy(right)} A${body.radius} ${body.radius} 0 1 1 ${xy(left)} A${body.radius} ${body.radius} 0 1 1 ${xy(right)} Z` };
    }
    // Both driving circles remain inside the outer bowl. A disc is convex, so
    // their common-tangent hull is contained too; clipping is only a safeguard.
    // Circular caps and exact common tangents give matching slopes at each join.
    const edge = tangents(body, tip);
    return {
        discs: [body, tip],
        tangents: [{ body: edge.bodyLeft, tip: edge.left }, { body: edge.bodyRight, tip: edge.right }],
        outline: `M${xy(edge.bodyLeft)} L${xy(edge.left)} A${tip.radius} ${tip.radius} 0 0 1 ${xy(edge.right)} L${xy(edge.bodyRight)} A${body.radius} ${body.radius} 0 1 1 ${xy(edge.bodyLeft)} Z`,
    };
}
export const flameOutline = flameConstruction(0).outline;
export const flameCore = flameCoreConstruction(flameConstruction(0)).outline;
// Plain geometric embers first appear over the upper surface, then lift clear.
// Their distinct core color makes the overlap visible without deforming the body.
export const flameEmber = 'M-0.25 -0.9 Q0 -1.15 0.25 -0.9 L0.9 -0.25 Q1.15 0 0.9 0.25 L0.25 0.9 Q0 1.15 -0.25 0.9 L-0.9 0.25 Q-1.15 0 -0.9 -0.25 Z';
// At 18–32 px, proportional scaling alone turns the dot into a subpixel.
// Give the diamond a roughly 4.5 px peak and the dot roughly 2.5 px, while
// allowing larger illustrations to keep their natural proportions.
function peakScales(size) {
    const pixels = Number.isFinite(size) ? Math.max(1, size) : 24;
    return { diamond: Math.max(2, 54 / pixels), dot: Math.max(2.4, 80 / pixels) };
}
export const emberSlots = 6;
export function restingEmbers(size = 24) {
    const scale = peakScales(size);
    // Keep resting diamonds upright too, without moving their tilted positions.
    const rotation = flameConstruction(0).rotation;
    return [
        { id: -1, shape: 'diamond', transform: `translate(16.05 3) rotate(${-rotation}) scale(${scale.diamond * 0.8})`, opacity: 1, orange: 1 },
        { id: -2, shape: 'dot', transform: `translate(12.6 0.8) scale(${scale.dot * 0.8})`, opacity: 1, orange: 1 },
        null, null, null, null,
    ];
}
const clamp = (value) => Math.max(0, Math.min(1, value));
// A stable hash keeps Replay deterministic while each new ember gets its own
// origin, curl, size and lifetime. This varies arrivals rather than looping a pair.
function variation(index, channel) {
    let value = Math.imul(index + 1, 0x45d9f3b) ^ Math.imul(channel + 1, 0x27d4eb2d);
    value = Math.imul(value ^ (value >>> 16), 0x45d9f3b);
    return ((value ^ (value >>> 16)) >>> 0) / 4294967296;
}
const births = [0, 0.48, 1.16, 1.7, 2.42, 2.92, 3.53];
const birthPeriod = 4.1;
export function flameEmbers(time, size, rotation, bursts = []) {
    const scale = peakScales(size);
    // Keep the larger optical sizes, but emit less often in compact icons so
    // readable embers don't crowd one another. Reuse six SVG slots; no DOM churn.
    const spacing = size <= 32 ? 1.12 : 1;
    const now = time + 1.7;
    const cycle = Math.floor(now / (birthPeriod * spacing));
    const particles = Array.from({ length: emberSlots }, () => null);
    for (let period = cycle - 1; period <= cycle; period++) {
        births.forEach((birth, index) => {
            const id = period * births.length + index;
            const start = (period * birthPeriod + birth + (variation(id, 0) - 0.5) * 0.08) * spacing;
            const lifetime = 1.05 + variation(id, 1) * 0.4;
            const age = now - start;
            if (age < 0 || age >= lifetime)
                return;
            const progress = age / lifetime;
            // Diamonds lead the visual rhythm; one-third discs add a softer accent.
            const shape = variation(id, 2) >= 1 / 3 ? 'diamond' : 'dot';
            // Alternate broad launch areas so consecutive large embers don't stack.
            // Position, curl and lifetime still vary within each area.
            const lane = [0, 2, 1][((id % 3) + 3) % 3];
            const x = 8.5 + lane * 2.9 + variation(id, 3) * 1.5;
            const y = 10.6 + variation(id, 4) * 1.5;
            const direction = lane - 1;
            const first = (variation(id, 5) - 0.5) * 2;
            const second = direction * 1.5 + (variation(id, 6) - 0.5) * 2.2;
            const end = direction * (0.5 + variation(id, 7));
            const inverse = 1 - progress;
            const drift = 3 * inverse * inverse * progress * first + 3 * inverse * progress * progress * second + progress ** 3 * end;
            const lift = 0.85 * progress + 0.15 * progress * progress;
            const rise = y + 1.5 + variation(id, 8);
            // Attach only at birth: later flame tilts must not swing an airborne
            // ember. Launch coordinates use the flame's angle at the actual birth
            // time (the emitter clock is offset by 1.7 s), around the same (12,15).
            const launchRotation = flameTilt(start - 1.7);
            const radians = launchRotation * Math.PI / 180;
            const sin = Math.sin(radians), cos = Math.cos(radians);
            const originX = 12 + (x - 12) * cos - (y - 15) * sin;
            const originY = 15 + (x - 12) * sin + (y - 15) * cos;
            const distance = lift * rise;
            // Fade the tilted velocity over the first 6 units of rise. Integrating
            // 1-smoothstep gives H*(q-q³+q⁴/2), capped at H/2: the sideways offset
            // settles instead of snapping or pulling back as the angle fades out.
            const q = clamp(distance / 6);
            const inheritedDistance = 6 * (q - q ** 3 + q ** 4 / 2);
            const freeX = originX + sin * inheritedDistance + drift;
            // After the first quarter of life, gradually return to the flame's
            // centreline. Quintic steering ends at x=12 with zero sideways velocity
            // and acceleration; the original curl remains visible during the rise.
            const returnProgress = clamp((progress - 0.25) / 0.75);
            const returnEase = returnProgress ** 3 * (10 + returnProgress * (-15 + 6 * returnProgress));
            const worldX = freeX + (12 - freeX) * returnEase;
            // Integrate a short vertical velocity pulse for emergence. Retain the
            // travelled distance after the pulse so live embers never snap back.
            // Subtract the integral at birth: newly emitted particles still begin
            // on the flame. Their usual sideways curl and upright shape are intact.
            const burstRise = bursts.reduce((total, burst) => {
                const integral = (at) => {
                    const q = clamp((at - burst.start) / 0.45);
                    return 36 * burst.strength * 0.45 * (q / 2 - Math.sin(2 * Math.PI * q) / (4 * Math.PI));
                };
                return total + integral(time) - integral(start - 1.7);
            }, 0);
            const worldY = originY - distance + (1 - cos) * inheritedDistance - burstRise;
            // A small swell peaks at 40% of life, then burns down. Smoothstep gives
            // both halves a shared zero slope at the peak, avoiding a size snap.
            // The 82% entry remains larger than the 62% finish for small-icon clarity.
            const scalePhase = progress < 0.4 ? progress / 0.4 : (progress - 0.4) / 0.6;
            const scaleEase = scalePhase * scalePhase * (3 - 2 * scalePhase);
            const scaleEnvelope = progress < 0.4 ? 0.82 + 0.18 * scaleEase : 1 - 0.38 * scaleEase;
            const particleScale = scale[shape] * (0.9 + variation(id, 9) * 0.3) * scaleEnvelope;
            // Hold yellow through the lift off the face, then blend during the higher climb.
            // Base this on distance risen so different lifetimes retain the same
            // visual handoff. Smoothstep avoids an abrupt start or end to the blend.
            const cooling = clamp((distance + burstRise - 5) / 4.5);
            particles[((id % emberSlots) + emberSlots) % emberSlots] = {
                id, shape, orange: cooling * cooling * (3 - 2 * cooling),
                // Cancel the live parent tilt so this world-space flight stays free;
                // keep diamonds upright for the flat graphic style. The birth angle
                // drives their flight only, independently of the shape's orientation.
                transform: `rotate(${-rotation} 12 15) translate(${worldX} ${worldY}) scale(${particleScale})`,
                opacity: clamp(progress / 0.07) * clamp((0.98 - progress) / 0.14),
            };
        });
    }
    return particles;
}
// Follow half the outer angle, 120 ms later. The core already changes shape
// through its tip motion; reducing rigid sway keeps that motion readable.
// Sampling the same eased tilt clock avoids a second
// spring/loop and keeps Replay, slow motion and interrupted transitions stable.
// Rotation about the outer bowl centre preserves core containment, because
// that containing disc is rotationally symmetric. Circle mode stays neutral.
export function flameCoreTilt(time, intensity = 1) {
    const level = Number.isFinite(intensity) ? clamp(intensity) : 1;
    return level === 0 ? 0 : flameTilt(time - 0.12) * 0.5 * level;
}
// A small, bounded cache shares the expensive tangent solve across instances
// at the exact same phase, regardless of icon size. All six geometric inputs
// participate: interrupted transitions or independent clocks cannot borrow a
// different pose. No time rounding, interpolation or frame-rate cap is used.
// Cached poses are read-only; the cache holds no DOM or component references.
function geometryAt(time, intensity, accent) {
    const construction = flameConstruction(time, intensity, accent);
    const coreLevel = accent?.coreIntensity ?? intensity;
    const core = flameCoreConstruction(construction, coreLevel, time, accent?.coreStretch);
    return { ...construction, core: core.outline, coreDiscs: core.discs,
        coreTangents: core.tangents, coreRotation: flameCoreTilt(time, coreLevel) };
}
const geometryCache = [];
let geometryCursor = 0;
export function flameGeometry(time, intensity = 1, accent) {
    const inputs = [time, intensity, accent?.tongueLift ?? 0, accent?.bodyScale ?? 1,
        accent?.coreStretch ?? 0, accent?.coreIntensity ?? intensity];
    const cached = geometryCache.find(entry => entry.inputs.every((value, index) => value === inputs[index]));
    if (cached)
        return cached.pose;
    const pose = geometryAt(time, intensity, accent);
    geometryCache[geometryCursor] = { inputs, pose };
    geometryCursor = (geometryCursor + 1) % 8;
    return pose;
}
export function flameMotion(time, size = 24, intensity = 1, accent, bursts) {
    const geometry = flameGeometry(time, intensity, accent);
    return { ...geometry, embers: flameEmbers(time, size, geometry.rotation, bursts) };
}
//# sourceMappingURL=flame-artwork.js.map